import { PagedListRequestInterface, PagedResponseInterface, PaginatedAPIInterface } from '@vendasta/galaxy/table';
import { from, Observable } from 'rxjs';

import { AiKnowledgeService } from '../services/ai-knowledge.service';
import { KnowledgeRowMapper } from './knowledge-row.mapper';
import { StaticTableApi } from './static-table.api';
import { TableKnowledgeBase } from './types';

/**
 * Server-backed paginated API for the Knowledge Collections table: the table's page size, cursor, and
 * search term drive the `listKnowledgeBases` query directly (no fetch-all). The endpoint has no sort, so
 * sorting isn't offered. In storybook / @Input mode (canned rows present) it falls back to in-memory paging
 * so stories keep working without a backend.
 */
export class KnowledgeBaseTableApi implements PaginatedAPIInterface<TableKnowledgeBase, unknown, unknown> {
  private readonly staticApi = new StaticTableApi<TableKnowledgeBase>(
    () => this.cannedRows() ?? [],
    (kb) => `${kb.name} ${kb.description} ${kb.managedBy}`,
  );

  constructor(
    private readonly knowledgeService: AiKnowledgeService,
    private readonly mapper: KnowledgeRowMapper,
    /** Canned rows for storybook / @Input mode, or null to fetch from the server. */
    private readonly cannedRows: () => TableKnowledgeBase[] | null,
  ) {}

  get(req: PagedListRequestInterface<unknown, unknown>): Observable<PagedResponseInterface<TableKnowledgeBase>> {
    if (this.cannedRows() !== null) {
      return this.staticApi.get(req);
    }
    return from(this.fetchPage(req));
  }

  private async fetchPage(
    req: PagedListRequestInterface<unknown, unknown>,
  ): Promise<PagedResponseInterface<TableKnowledgeBase>> {
    const pageSize = req.pagingOptions?.pageSize ?? 20;
    const cursor = req.pagingOptions?.cursor ?? '';
    const searchTerm = (req.searchOptions?.text ?? '').trim();
    const callerNs = this.knowledgeService.buildNamespaceInterface();
    const page = await this.knowledgeService.listKnowledgeBasesPage(searchTerm, cursor, pageSize);
    const data = await Promise.all(page.knowledgeBases.map((kb) => this.mapper.toKbRow(kb, callerNs)));
    return {
      data,
      pagingMetadata: { nextCursor: page.nextCursor, hasMore: page.hasMore, totalResults: page.totalResults },
    };
  }
}
